import React from 'react';

/**
 * Mapeo exacto de estilos para los chips de estado.
 * Fondo blanco, bordes definidos y texto coloreado.
 */
export const getStatusBadgeStyle = (status) => {
    switch ((status || '').toUpperCase()) {
        case 'VIGENTE': 
            return 'border border-green-600 text-green-700 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
        case 'PENDIENTE DE EMISIÓN': 
            return 'border border-orange-500 text-orange-600 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
        case 'PENDIENTE DE INSPECCIÓN': 
            return 'border border-orange-700 text-orange-800 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
        case 'PROPUESTA INGRESADA': 
            return 'border border-purple-600 text-purple-700 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
        case 'VENCIDA': 
            return 'border border-red-600 text-red-700 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
        case 'ANULADA': 
            return 'border border-red-800 text-red-900 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
        case 'NO INICIADA': 
            return 'border border-blue-600 text-blue-700 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
        default: 
            return 'border border-gray-400 text-gray-600 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200';
    }
};

/**
 * FilterBar - Componente modular para la barra de resultados y filtros.
 */
const FilterBar = ({
    totalResults = 0,
    filters = {},
    onFilterChange,
    onClearFilter,
    onClearAllFilters,
    showFilters,
    setShowFilters,
    options = {
        estados: [],
        companias: [],
        riesgos: [],
        motivos: []
    },
    showPatenteFilter = true
}) => {
    return (
        <div className="space-y-4 w-full select-none text-left">
            
            {/* BARRA SUPERIOR DE RESULTADOS Y CONTADORES */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-200/80 pb-4">
                <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">
                        Resultados
                    </h2>

                    {/* CONTADOR TOTAL ESTILO PÍLDORA BLANCA */}
                    <span className="bg-white text-slate-800 text-[11px] font-extrabold px-3.5 py-1.5 rounded-full border border-slate-200 shadow-2xs flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        {totalResults} encontrados
                    </span>

                    {/* CHIP/BADGE DINÁMICO CON TUS ESTILOS EXACTOS */}
                    {filters.estado && (
                        <button
                            type="button"
                            onClick={() => onClearFilter('estado')}
                            title="Haz clic para quitar este filtro"
                            className={`inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[11px] font-black uppercase tracking-wide shadow-2xs transition-all cursor-pointer group ${getStatusBadgeStyle(filters.estado)}`}
                        >
                            <span>{filters.estado}</span>
                            <span className="text-xs group-hover:inline hidden">✕</span>
                        </button>
                    )}
                </div>

                {/* BOTÓN DESPLEGABLE */}
                <button 
                    type="button"
                    onClick={() => setShowFilters(!showFilters)} 
                    className={`px-4 py-2 rounded-xl border font-extrabold text-xs transition-all shadow-2xs cursor-pointer flex items-center gap-2 ${
                        showFilters 
                            ? 'bg-slate-900 text-white border-slate-900' 
                            : 'bg-white text-slate-700 border-slate-200/90 hover:bg-slate-50'
                    }`}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span>{showFilters ? 'Ocultar Filtros' : 'Filtrar Resultados'}</span>
                </button>
            </div>

            {/* PANEL DE FILTROS DESPLEGABLE */}
            {showFilters && (
                <div className="bg-slate-50/90 border border-slate-200/80 rounded-3xl p-6 shadow-2xs space-y-4 animate-slide-down">
                    <div className="flex items-center justify-between border-b border-slate-200/60 pb-3">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                            Filtros de Precisión Local
                        </span>
                        
                        <button 
                            type="button"
                            onClick={onClearAllFilters}
                            className="text-[10px] font-extrabold text-blue-600 hover:text-rose-600 uppercase tracking-wider transition-colors cursor-pointer"
                        >
                            Limpiar Todo ↺
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        
                        {/* SELECT ESTADO */}
                        <div className="bg-white p-2.5 px-3.5 rounded-2xl border border-slate-200/70 shadow-2xs space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                🟢 Estado Real
                            </label>
                            <select 
                                value={filters.estado || ''} 
                                onChange={e => onFilterChange('estado', e.target.value)} 
                                className="w-full font-bold text-xs text-slate-800 outline-none bg-transparent cursor-pointer"
                            >
                                <option value="">Todos los Estados</option>
                                {options.estados.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>

                        {/* SELECT COMPAÑÍA */}
                        <div className="bg-white p-2.5 px-3.5 rounded-2xl border border-slate-200/70 shadow-2xs space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                🏢 Compañía
                            </label>
                            <select 
                                value={filters.compania || ''} 
                                onChange={e => onFilterChange('compania', e.target.value)} 
                                className="w-full font-bold text-xs text-slate-800 outline-none bg-transparent cursor-pointer"
                            >
                                <option value="">Todas las Aseguradoras</option>
                                {options.companias.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>

                        {/* SELECT RIESGO */}
                        <div className="bg-white p-2.5 px-3.5 rounded-2xl border border-slate-200/70 shadow-2xs space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                🛡️ Ramo / Riesgo
                            </label>
                            <select 
                                value={filters.riesgo || ''} 
                                onChange={e => onFilterChange('riesgo', e.target.value)} 
                                className="w-full font-bold text-xs text-slate-800 outline-none bg-transparent cursor-pointer"
                            >
                                <option value="">Todos los Riesgos</option>
                                {options.riesgos.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>

                        {/* SELECT MOTIVO */}
                        <div className="bg-white p-2.5 px-3.5 rounded-2xl border border-slate-200/70 shadow-2xs space-y-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                📋 Motivo Carga
                            </label>
                            <select 
                                value={filters.motivo || ''} 
                                onChange={e => onFilterChange('motivo', e.target.value)} 
                                className="w-full font-bold text-xs text-slate-800 outline-none bg-transparent cursor-pointer"
                            >
                                <option value="">Todos los Motivos</option>
                                {options.motivos.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>

                        {/* INPUT PATENTE */}
                        {showPatenteFilter && (
                            <div className="bg-white p-2.5 px-3.5 rounded-2xl border border-slate-200/70 shadow-2xs space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                    🚘 Dominio / Patente
                                </label>
                                <input 
                                    type="text" 
                                    value={filters.patente || ''} 
                                    onChange={e => onFilterChange('patente', e.target.value)} 
                                    className="w-full font-bold text-xs text-slate-800 outline-none bg-transparent uppercase placeholder:font-normal placeholder:text-slate-300" 
                                    placeholder="AA123CD..." 
                                />
                            </div>
                        )}

                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterBar;